import { useCallback, useLayoutEffect, useRef, useState } from "react";
/* TODO: document hook */
/**
 * This tracks focus state (whether it's inside or outside the container)
 * and returns a flag that says if focus should be restored to the container whenever a nested component re-mounts itself
 */
export function useFocusOnMount({ initialValue = false, containerRef }) {
    const [isFocused, setIsFocused] = useState(false);
    const [isNavigating, setIsNavigating] = useState(initialValue);
    const interactionHandler = useRef(undefined);
    const createInteractionHandler = () => {
        // Keep isNavigating and let the consumer do something with it (focus the new component instance or at least the loading indicator)
        const interactionHandlerFn = () => {
            document.body.removeEventListener("focusin", interactionHandlerFn);
            document.body.removeEventListener("click", interactionHandlerFn);
            setIsNavigating(false);
            interactionHandler.current = undefined;
        };
        interactionHandler.current = interactionHandlerFn;
        document.body.addEventListener("focusin", interactionHandlerFn, { once: true });
        document.body.addEventListener("click", interactionHandlerFn, { once: true });
    };
    const handleFocus = useCallback(() => {
        setIsFocused(true);
        interactionHandler.current?.();
    }, []);
    const handleBlur = useCallback((e) => {
        if (containerRef.current && containerRef.current.contains(e.relatedTarget)) {
            // Ignore tabbing inside the container
            return;
        }
        setIsFocused(false);
        if (isNavigating && e.relatedTarget === null) {
            // Focus was lost to body most probably due to element removal after navigation was initiated;
            if (!interactionHandler.current) {
                createInteractionHandler();
            }
        }
        else {
            setIsNavigating(false);
        }
    }, [containerRef, isNavigating]);
    const handleMaybeNavigate = useCallback((force) => {
        setIsNavigating(true);
        if (force ||
            (!isFocused && !interactionHandler.current) // Focus already lost before we started navigating or is a button click in Safari
        ) {
            createInteractionHandler();
        }
    }, [isFocused]);
    useLayoutEffect(() => () => {
        if (interactionHandler.current) {
            document.body.removeEventListener("focusin", interactionHandler.current);
            document.body.removeEventListener("click", interactionHandler.current);
            interactionHandler.current = undefined;
        }
    }, []);
    return { shouldFocusOnMount: isFocused || isNavigating, handleFocus, handleBlur, handleMaybeNavigate };
}
